import PropTypes from 'prop-types';
import { includes, filter, length, path, sort } from 'ramda';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { readEndpoint } from 'redux-json-api';
import { createSelector } from 'reselect';

import Button from '@material-ui/core/Button';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/styles';

import SelectField from '../../components/fields/SelectField';
import SwitchField from '../../components/fields/SwitchField';
import TextField from '../../components/fields/TextField';

import ServicedRailwaysField from '../fields/ServicedRailwaysField';
import { isAdmin, isSubcontractor, selectApiUsers } from '../../modules/selectors/permissions';


class CompanyForm extends Component {
  static propTypes = {
    // Own props.
    companyId: PropTypes.string,

    // State props.
    isAdmin: PropTypes.bool.isRequired,
    isSubcontractor: PropTypes.bool.isRequired,
    railways: PropTypes.array.isRequired,
    subcontractors: PropTypes.array.isRequired,

    // Dispatch props.
    readEndpoint: PropTypes.func.isRequired,

    // redux-form props.
    handleSubmit: PropTypes.func.isRequired,

    // own form-props
    handleDelete: PropTypes.func.isRequired,
  };

  componentWillMount() {
    const { companyId, initialValues, readEndpoint } = this.props;

    if (companyId && initialValues) {
      readEndpoint('api-user?filter[deleted-at__isnull]=True');
    }
  }

  render() {
    const {
      // redux-form props
      classes, error, handleSubmit, initialValues, pristine, submitting,
      // redux state props
      isAdmin, isSubcontractor, railways, subcontractors,
      // own form-props
      handleDelete,
    } = this.props;

    return (
      <form onSubmit={handleSubmit}>
        <CardContent>
          {error && <Typography variant="subtitle1">{error}</Typography>}

          {isAdmin && initialValues && (
            <Field
              name="is_active"
              component={SwitchField}
              label="Actief"
              id="company_form_is_active"
            />
          )}
        </CardContent>

        {initialValues && (
          <Fragment>
            <CardContent>
              <Field name="active_subcontractor" component={SelectField} label="Actieve onderaannemer" id="company_form_active_subcontractor">
                {subcontractors.map(sc => (
                  <MenuItem
                    key={'subcontractor-' + sc.id}
                    value={sc.id}
                  >
                    {`${sc.attributes['first-name']} ${sc.attributes['last-name']}`}
                  </MenuItem>
                ))}
              </Field>
            </CardContent>

            <CardHeader title="Eigenschappen" />

            <Divider />
          </Fragment>
        )}

        <CardContent>
          <Field name="name" component={TextField} label="Naam bedrijf" fullWidth id="company_form_name" />
          <Field name="address" component={TextField} label="Adres" fullWidth id="company_form_address" />
          <Field name="zipcode" component={TextField} label="Postcode" fullWidth id="company_form_zipcode" />
          <Field name="city" component={TextField} label="Plaats" fullWidth id="company_form_city" />
          <Field name="country" component={SelectField} label="Land" id="company_form_country" >
            <MenuItem value="" disabled>Kies een land...</MenuItem>
            <MenuItem value="nl">Nederland</MenuItem>
            <MenuItem value="de">Duitsland</MenuItem>
            <MenuItem value="be">België</MenuItem>
          </Field>
          <Field name="phone" component={TextField} label="Telefoonnummer" fullWidth id="company_form_phone" />
          <Field name="phone_24h" component={TextField} label="Telefoonnummer (24-uurs)" fullWidth id="company_form_phone_24h" />
          <Field name="phone_24h_2" component={TextField} label="Tweede telefoonnummer (24-uurs)" fullWidth id="company_form_phone_24h_2" />
          <Field name="email" component={TextField} label="E-mailadres" fullWidth id="company_form_email" />
          <Field
            component={TextField}
            fullWidth
            id="company_form_website"
            label="Website"
            name="website"
            normalize={(value) => {
              if (length(value) > 4 && value.substring(0, 4).toLowerCase() != 'http') {
                return `https:\/\/${value}`;
              }

              return value;
            }}
          />

          <br />

          <Typography variant="subtitle1" component="b">Betalingsgegevens</Typography>

          <Divider />

          <br />

          <Field name="bank_account" component={TextField} label="Bankrekeningnummer" fullWidth id="company_form_bank_account" />
          <Field name="kvk_number" component={TextField} label="KvK-nummer" fullWidth id="company_form_kvk_number" />

          <Typography variant="subtitle1" component="b">Trajecten</Typography>

          <Divider />

          {isAdmin ? (
            <Field
              name="serviced_railways"
              component={ServicedRailwaysField}
              railways={railways}
              label="Trajecten"
              fullWidth
            />
          ) : (
            <Fragment>
              <Table>
                <TableBody>
                  {railways.map(railway => {
                    if (!initialValues || !initialValues.serviced_railways ||
                      !includes(String(railway.id), initialValues.serviced_railways))
                      return null;

                    const name = path(['attributes', 'name'], railway) || 'Onbekend';

                    return (
                      <TableRow key={'railway-' + railway.id}>
                        <TableCell>{name}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <br />

              <em>Deze trajecten zijn alleen in te stellen door wachtdienstmedewerkers van Arriva Touring</em>
            </Fragment>
          )}

          <CardActions className={classes.cardActions}>
            {(isAdmin || isSubcontractor) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDelete()}
              >
                Verwijderen bedrijf
              </Button>
            )}
            {(isAdmin || isSubcontractor) && (
              <Button
                disabled={pristine || submitting}
                variant="contained"
                color="primary"
                type="submit"
              >
                {initialValues ? 'Wijzig bedrijf' : 'Voeg bedrijf toe'}
              </Button>
            )}
          </CardActions>
        </CardContent>
      </form>
    );
  };
}


/**
 * Redux state selectors.
 */
const selectCompanyId = (state, ownProps) => (ownProps.companyId);
const selectRailways = state => (path(['api', 'railway', 'data'], state) || []);

/**
 * Redux selector that takes advantage of reselect's createSelector to memoize
 * the result.
 */
const getSubcontractors = createSelector([selectCompanyId, selectApiUsers], (companyId, apiUsers) => {
  if (!companyId) return [];

  return filter(apiUser => {
    const cids = (path(['relationships', 'as-subcontractor', 'data'], apiUser) || []).map(c => Number(c.id));

    return includes(Number(companyId), cids);
  }, apiUsers);
});

const getSortedActiveRailways = createSelector([selectRailways], railways => {
  const isActive = railway => {
    const active = path(['attributes', 'active'], railway);

    if (active === true) return true;
    else if (active === false) return false;
    else return true; // Fallback for undefined.
  };
  const diff = (a, b) => {
    const a_s = path(['attributes', 'sorting'], a);
    const b_s = path(['attributes', 'sorting'], b);

    return a_s - b_s;
  };

  const activeRailways = filter(isActive, railways);
  const sortedActiveRailways = sort(diff, activeRailways);

  console.group('railways');
  sortedActiveRailways.forEach(r => {
    console.log('<' + r.attributes.sorting + '> ' + r.attributes.name);
  });
  console.groupEnd();

  return sortedActiveRailways;
});

const mapStateToProps = (state, ownProps) => ({
  isAdmin: isAdmin(state),
  isSubcontractor: isSubcontractor(state),
  railways: getSortedActiveRailways(state),
  subcontractors: getSubcontractors(state, ownProps),
});

const mapDispatchToProps = {
  readEndpoint,
};

CompanyForm = reduxForm({
  form: 'companyForm',
  validate: (values) => {
    const errors = {};

    if (!values.name) errors.name = 'Verplicht (voorbeeld: Arriva Touring)';
    if (!values.address) errors.address = 'Verplicht (voorbeeld: Bornholmstraat 60)';
    if (!values.zipcode) errors.zipcode = 'Verplicht (voorbeeld: 9723 AZ)';
    if (!values.city) errors.city = 'Verplicht (voorbeeld: Groningen)';
    if (!values.country) errors.country = 'Verplicht (kies een land uit de lijst)';
    if (!values.phone) errors.phone = 'Verplicht (voorbeeld: 050 526 0268)';
    if (!values.phone_24h) errors.phone_24h = 'Verplicht (voorbeeld: 06 12345678)';
    if (!values.email) errors.email = 'Verplicht (voorbeeld: info@arrivatouring.nl)';
    if (!values.website) errors.website = 'Verplicht (voorbeeld: https://www.arrivatouring.nl)';

    if (!values.bank_account) errors.bank_account = 'Verplicht (voorbeeld: NL54RABO0383008336)';

    return errors;
  },
})(CompanyForm);

export default connect(mapStateToProps, mapDispatchToProps)(withStyles({
  cardActions: {
    justifyContent: 'flex-end',
    marginTop: 20,
  },
})(CompanyForm));
